import { KpiSigns } from '../components/KpiCard/types';
import { KPITypesEnum } from '../types';
import { TimeRangeEnum } from '../components/FilterSection/TimeRangeSelector/types';

export function formatMetricLabel(input: string): string {
  const uppercaseMetrics = ['aov', 'arpu', 'cvr', 'ctr'];

  if (uppercaseMetrics.includes(input.toLowerCase())) {
    return input.toUpperCase();
  }

  let label = input.replace(/_/g, ' ');
  label = label.replace(/([a-z])([A-Z])/g, '$1 $2');
  label = label.replace(/\b\w/g, char => char.toUpperCase());
  return label;
}

export function camelToSnakeCase(input: string): string {
  return input.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
}

export function formatNumberShort(n: number | string): string {
  const num = Number(n);
  if (isNaN(num)) return '-';

  const absNum = Math.abs(num);

  const withSuffix = (value: number, suffix: string) => {
    const rounded = value >= 10 ? value.toFixed(0) : value.toFixed(2);
    return `${rounded}${suffix}`;
  };

  if (absNum >= 1_000_000_000) return withSuffix(num / 1_000_000_000, 'B');
  if (absNum >= 1_000_000) return withSuffix(num / 1_000_000, 'M');
  if (absNum >= 1_000) return withSuffix(num / 1_000, 'K');

  return num.toFixed(2).toLocaleString();
}
export function formatKpiWithSign(kpiType: KPITypesEnum, value?: number): string {
  if (value == null) return '-';

  const sign = KpiSigns[kpiType] || '';

  const isPercentageMetric = [KPITypesEnum.CVR, KPITypesEnum.CTR].includes(kpiType);
  const adjustedValue = isPercentageMetric ? value * 100 : value;

  const formattedValue = formatNumberShort(adjustedValue);

  return sign === '$' ? `${sign}${formattedValue}` : `${formattedValue}${sign}`;
}

export function getPreviousMonthsDateRange(timeRange: TimeRangeEnum): string {
  const formatUtc = (d: Date) =>
    `${d.getUTCFullYear()}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}`;

  const getUtcMonday = (date: Date) => {
    const utcDate = new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
    );
    const dayOfWeek = utcDate.getUTCDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    utcDate.setUTCDate(utcDate.getUTCDate() + diff);
    return utcDate;
  };

  if (timeRange === TimeRangeEnum.Last1Day) {
    const now = new Date();
    const utcToday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const end = new Date(utcToday);
    end.setUTCDate(utcToday.getUTCDate() - 1);
    const start = new Date(end);

    return `${formatUtc(start)} (UTC)`;
  }

  if (timeRange === TimeRangeEnum.Last1Week) {
    const now = new Date();
    const thisMondayUTC = getUtcMonday(now);
    const lastMondayUTC = new Date(thisMondayUTC);
    lastMondayUTC.setUTCDate(thisMondayUTC.getUTCDate() - 7);
    const lastSundayUTC = new Date(thisMondayUTC);
    lastSundayUTC.setUTCDate(thisMondayUTC.getUTCDate() - 1);

    return `${formatUtc(lastMondayUTC)} - ${formatUtc(lastSundayUTC)}`;
  }

  if (timeRange === TimeRangeEnum.Last30Days) {
    const now = new Date();
    const utcToday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const end = new Date(utcToday);
    end.setUTCDate(utcToday.getUTCDate() - 1);
    const start = new Date(end);
    start.setUTCDate(end.getUTCDate() - 29);

    return `${formatUtc(start)} - ${formatUtc(end)}`;
  }

  const match = timeRange.match(/^last_(\d+)_month[s]?$/);
  if (!match) {
    throw new Error(
      'Invalid timeRange format. Use e.g. TimeRangeEnum.Last1Day, TimeRangeEnum.Last1Week, TimeRangeEnum.Last1Month'
    );
  }
  const months = parseInt(match[1], 10);
  if (months < 1) {
    throw new Error('Number of months must be at least 1');
  }

  const now = new Date();
  const startOfCurrentMonthUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const endOfPreviousMonthUTC = new Date(startOfCurrentMonthUTC);
  endOfPreviousMonthUTC.setUTCDate(0);
  const startOfRangeUTC = new Date(
    Date.UTC(
      endOfPreviousMonthUTC.getUTCFullYear(),
      endOfPreviousMonthUTC.getUTCMonth() - (months - 1),
      1
    )
  );

  return `${formatUtc(startOfRangeUTC)} - ${formatUtc(endOfPreviousMonthUTC)}`;
}
